import axios from "axios";
import { mitHerkunft } from "./herkunft"; // MESS-09a, Betreiberfreigabe 01.11.2026
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// AUTH-SICHERHEIT V2 · Phase 2 (Feb 2026, betreibergenehmigt): `withCredentials`
// sendet die Session-Cookies (`owner_session_cookie`, `employee_session_cookie`)
// automatisch bei jeder API-Anfrage mit. Der explizite Bearer-Header aus dem
// Interceptor in `useEmployeeSession.jsx` fällt in derselben Phase weg. Backend
// akzeptiert weiterhin beide Auth-Quellen (Dual-Modus bis Phase 4).
export const client = axios.create({ baseURL: API, withCredentials: true });

export const api = {
  getCompany: () => client.get("/company").then((r) => r.data),
  // Chris C3 (Okt 2026) – Handlungstrigger-Snapshot (aktiv? + aggregierte Zahl),
  // serverseitige Cooldowns + C2A-Schalter in EINEM konsistenten Read.
  getChrisTriggers: () => client.get("/chris/triggers").then((r) => r.data),
  // Quittung "Sprechblase TATSÄCHLICH gezeigt" → setzt feld-genau
  // companies.chris_cooldowns.<event_key> (Serverzeit). Best-effort.
  markChrisShown: (eventKey) =>
    client.post("/chris/shown", { event_key: eventKey }).then((r) => r.data),
  // Chris C4 „Auf Abruf" (Okt 2026) – verdichtete Zusammenfassung zuletzt/jetzt/nächstes.
  // REIN LESEND, cooldown-unabhängig; löst NIEMALS /chris/shown oder einen Cooldown-Write aus.
  getChrisSummary: () => client.get("/chris/summary").then((r) => r.data),
  // M4.3 (Juni 2026) – öffentlicher Produktkatalog für die Landing. ANONYM (kein
  // Owner-Token): bewusst globales `axios` statt `client` (dessen Interceptor hängt
  // den Owner-Token an). Exposure-Guard liegt serverseitig — solange keine Version
  // public_visible ist, liefert die API {published:false, products:[]}.
  getPublicCatalog: () => axios.get(`${API}/catalog`).then((r) => r.data),
  // P9.2 (Juni 2026) – Billing-Transparenz (read-only, kein Stripe/Geldfluss).
  getBillingSummary: () => client.get("/billing/summary").then((r) => r.data),
  // P9.3 (Juni 2026) – Tarifvergleich + Wechsel-ANFRAGE (kein echter Wechsel, kein Geldfluss).
  getPlanComparison: () => client.get("/billing/plans").then((r) => r.data),
  createPlanChangeRequest: (payload) =>
    client.post("/billing/change-request", payload).then((r) => r.data),
  getPlanChangeRequests: () => client.get("/billing/change-requests").then((r) => r.data),
  cancelPlanChangeRequest: (id) =>
    client.post(`/billing/change-requests/${id}/cancel`).then((r) => r.data),
  // R6-0c (Juni 2026) – App-Checkout: erzeugt serverseitig eine Stripe-Checkout-Session
  // (mode=subscription). Body NUR `origin_url`; Preis/Produkt/Stripe-Parameter kommen
  // ausschließlich aus Backend/Resolver (Frontend bleibt "dumm"). Antwort {url, session_id, reused}.
  createCheckout: (origin_url) =>
    client.post("/billing/checkout", { origin_url }).then((r) => r.data),
  // ZAHL-02b (Betreiberfreigabe 13.10., Z2) – Stripe-Kundenportal: ziel "zahlungsdaten" |
  // "rechnungen"; Antwort {url}. Kein Stripe im Frontend, nur Weiterleitung auf die URL.
  createPortalSession: (ziel, origin_url) =>
    client.post("/billing/portal-session", { ziel, origin_url }).then((r) => r.data),
  // MOD-01b-3 (Betreiberfreigabe 13.10.) – Zusatzmodule (Owner): Übersicht + Hinzubuchen /
  // Abbestellen / Zurücknehmen ausschließlich über die Backend-Endpunkte (kein Stripe im Frontend).
  getBillingModules: () => client.get("/billing/modules").then((r) => r.data),
  bookModule: (moduleId) => client.post(`/billing/modules/${moduleId}/book`).then((r) => r.data),
  cancelModule: (moduleId) => client.post(`/billing/modules/${moduleId}/cancel`).then((r) => r.data),
  undoCancelModule: (moduleId) =>
    client.post(`/billing/modules/${moduleId}/undo-cancel`).then((r) => r.data),
  // D12 (Tür 1) – ANONYMER Einmalkauf (`einmal_pdf`). Kein Owner-Token (globales axios).
  // Content = die gesehene, read-only Vorschau; Preis/Produkt kommen serverseitig aus dem
  // Katalog. Antwort {url, session_id, reused}.
  // O1C: product_id des GELADENEN Katalogprodukts ist Pflicht (kein Preis, keine Stripe-ID).
  // MESS-09a, Betreiberfreigabe 01.11.2026: Herkunft des Tabs über mitHerkunft (ohne Herkunft Körper wie bisher).
  createOneTimeCheckout: ({ origin_url, content, company_name, product_id }) =>
    axios
      .post(`${API}/billing/one-time-checkout`, mitHerkunft({ origin_url, content, company_name, product_id }))
      .then((r) => r.data),
  getOneTimeStatus: (sessionId) =>
    axios.get(`${API}/billing/one-time-status/${sessionId}`).then((r) => r.data),
  listEmployees: () => client.get("/employees").then((r) => r.data),
  getEmployee: (id) => client.get(`/employees/${id}`).then((r) => r.data),
  listCourses: (includePending = false, includeArchived = false) => {
    const params = {};
    if (includePending) params.include_pending = true;
    // WELT-ARCHIVIERUNG (Feb 2026, F1–F5): archivierte Welten werden per
    // default AUSGEBLENDET. Bibliothek reicht den Filter durch, wenn der
    // Nutzer im „Archivierte"-Chip filtert.
    if (includeArchived) params.include_archived = true;
    return client.get("/courses", { params }).then((r) => r.data);
  },
  listJobsActive: () => client.get("/jobs/active").then((r) => r.data),
  getCourse: (id) => client.get(`/courses/${id}`).then((r) => r.data),
  // PRINT.1 (Juni 2026) – read-only Owner-Display-Preview fürs Plakat (atomarer
  // Read-Resolver-Pfad). Liefert {requested_language, served_language, display}.
  getPlakatDisplay: (id, lang) =>
    client
      .get(`/courses/${id}/plakat-display`, { params: { lang } })
      .then((r) => r.data),
  // PLAKAT-PDF (2026-06) – serverseitiger Chromium-PDF-Download des Plakats
  // (Owner). Liefert die VOLLE Response (Blob + Content-Disposition-Header).
  downloadPlakatPdf: (id, { format, orientation, lang }) =>
    client.get(`/courses/${id}/plakat-pdf`, {
      params: { format, orientation, lang },
      responseType: "blob",
    }),
  // PLAKAT-PDF (2026-06) – interner Datenendpunkt der Plakat-Druckroute
  // (Chromium). ANONYM + zweckgebundenes kurzlebiges Render-Token — bewusst
  // globales `axios` (kein Owner-Token im Renderer, Betreiberfreigabe 2b).
  getPlakatDruckDaten: (token) =>
    axios.get(`${API}/druck/plakat-daten/${token}`).then((r) => r.data),
  listAssignments: (params = {}) =>
    client.get("/assignments", { params }).then((r) => r.data),
  getAssignment: (id) => client.get(`/assignments/${id}`).then((r) => r.data),
  createAssignment: (data) =>
    client.post("/assignments", data).then((r) => r.data),
  listAssignmentBatches: () =>
    client.get("/assignments").then((r) => r.data),
  completeAssignment: (id, answers) =>
    client
      .post(`/assignments/${id}/complete`, { answers })
      .then((r) => r.data),
  listNachweise: () => client.get("/nachweise").then((r) => r.data),
  getNachweis: (id) => client.get(`/nachweise/${id}`).then((r) => r.data),
  // PDF-Download: serverseitig erzeugtes, prüfsicheres WeasyPrint-PDF
  // (KEIN Browser-Druck – ohne App-Navigation/Browser-Kopf-/Fußzeilen).
  // Der axios-Interceptor hängt das Owner-Token an (Owner-Route).
  downloadNachweisPdf: async (id) => {
    const res = await client.get(`/nachweise/${id}/pdf`, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : `${id}.pdf`;
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return filename;
  },
  // BG-Prüfpaket (ZIP): byte-exakte WORM-Original-PDFs aus S3 + CSV + Audit-JSON.
  // Reiner Download; der axios-Interceptor hängt das Owner-Token an.
  downloadBgPruefpaket: async () => {
    const res = await client.get(`/nachweise/bg-pruefpaket`, {
      responseType: "blob",
    });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const filename = m ? m[1] : "BG-Pruefpaket.zip";
    const url = URL.createObjectURL(
      new Blob([res.data], { type: "application/zip" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return filename;
  },
  getStats: () => client.get("/dashboard/stats").then((r) => r.data),
  // P1 (Juni 2026) – Wirksamkeitskontrolle (W-A1): Owner-Read + Bestätigung.
  // KEIN PDF/WORM. Bestätigung dokumentiert eine tatsächlich durchgeführte
  // Beobachtung des Unternehmers (CLartext bewertet nichts).
  getWirksamkeitSummary: () =>
    client.get("/wirksamkeit/summary").then((r) => r.data),
  listOpenWirksamkeit: () => client.get("/wirksamkeit/open").then((r) => r.data),
  // Schritt 6 (NUR Darstellung): erledigte WK für die Prüfmappe (read-only).
  listErledigteWirksamkeit: () =>
    client.get("/wirksamkeit/erledigt").then((r) => r.data),
  // PDF (byte-exaktes WORM-Original) im neuen Tab öffnen. Owner-Token via
  // axios-Interceptor; ausschließlich Auslieferung des Archiv-Originals.
  openWirksamkeitPdf: async (id) => {
    const res = await client.get(`/wirksamkeit/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return url;
  },
  getWirksamkeit: (id) => client.get(`/wirksamkeit/${id}`).then((r) => r.data),
  confirmWirksamkeit: (id, payload) =>
    client.post(`/wirksamkeit/${id}/confirm`, payload).then((r) => r.data),
  generateCourse: (topic, context = null) => {
    // P2 Schritt 4 (Juni 2026): OPTIONALER Voranalyse-Kontext (rein beratend).
    // Wird NUR angehängt, wenn nicht-leere Daten vorliegen. Ohne Kontext bleibt
    // der Request byte-identisch ({ topic, force_world: true }).
    const body = { topic, force_world: true };
    if (context) {
      if (Array.isArray(context.clarifying_answers) && context.clarifying_answers.length > 0)
        body.clarifying_answers = context.clarifying_answers;
      if (Array.isArray(context.candidate_activity_classes) && context.candidate_activity_classes.length > 0)
        body.candidate_activity_classes = context.candidate_activity_classes;
      if (Array.isArray(context.uncertain_activity_classes) && context.uncertain_activity_classes.length > 0)
        body.uncertain_activity_classes = context.uncertain_activity_classes;
      // P3.1 Schritt 2 (Juni 2026): optionaler Produkt-Kontext (rein beratend).
      if (context.product_context && typeof context.product_context === "object"
          && Object.keys(context.product_context).length > 0)
        body.product_context = context.product_context;
      // Phase A · Schritt D (Gefahrstoffmodul, 22.08.2026): drei zusätzliche,
      // rein additive Felder. Fehlen sie, bleibt der Request byte-identisch
      // zum bisherigen Aufruf.
      if (context.hazmat_declaration && typeof context.hazmat_declaration === "object") {
        body.hazmat_declaration = context.hazmat_declaration;
      }
      if (context.preanalysis_had_hazmat_signal === true) {
        body.preanalysis_had_hazmat_signal = true;
      }
      if (context.flee_hazmat === true) {
        body.flee_hazmat = true;
      }
      // Phase C · Etappe 2 (Gefahrstoffmodul, 22.08.2026): optionale
      // SDB-Prefetch-ID aus dem F5a Sichtungs-Modal. Wenn gesetzt, lädt
      // das Backend PDF+Extract aus dem 24h-Cache, archiviert den
      // WORM-Zeugen und reicht den Extrakt als sdb_context an die
      // MP-3.6-Weltgenerierung durch.
      if (typeof context.sdb_prefetch_id === "string" && context.sdb_prefetch_id) {
        body.sdb_prefetch_id = context.sdb_prefetch_id;
      }
    }
    return client.post("/courses/generate", body).then((r) => r.data);
  },
  // Phase C · Etappe 2 (Gefahrstoffmodul, 22.08.2026): SDB-Suche als
  // ASYNC-Job. Startet den Hintergrund-Task (POST 202) und liefert die
  // job_id zurück. Frontend pollt dann `getSdbFetchExtractStatus`.
  startSdbFetchExtract: (productName, manufacturer) => {
    const body = { product_name: productName };
    if (manufacturer) body.manufacturer = manufacturer;
    return client.post("/courses/sdb-fetch-extract", body).then((r) => r.data);
  },
  getSdbFetchExtractStatus: (jobId) =>
    client.get(`/courses/sdb-fetch-extract/${encodeURIComponent(jobId)}`).then((r) => r.data),
  // Betreiberentscheidung 24.08.2026 · Drag-&-Drop-Zweig des kombinierten
  // Modals. Der Anwender lädt das SDB selbst hoch — Content-Type/Größe/Magic
  // werden serverseitig geprüft, sdb_extractor läuft, Response-Shape ist
  // identisch zum Auto-Fund.
  uploadSdbManual: (file, productName, manufacturer) => {
    const form = new FormData();
    form.append("file", file);
    form.append("product_name", productName);
    form.append("manufacturer", manufacturer || "");
    return client.post("/courses/sdb-manual-upload", form).then((r) => r.data);
  },
  // P2 Schritt 1/2: zustandslose Voranalyse (kein Course, keine Generierung, kein Persist).
  preAnalyze: (topic) =>
    client.post("/courses/pre-analyze", { topic }).then((r) => r.data),
  // P3.2.2-b (Juni 2026): ephemere Typenschild-Analyse. Sendet GENAU EIN Bild
  // (multipart) an den fail-closed Vision-Endpoint; liefert nur Vorschläge +
  // Provenienz zurück. KEIN Persist, kein product_context – reine Anzeige.
  analyzeProductEvidence: (file) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/product-evidence/analyze", form).then((r) => r.data);
  },
  releaseCourse: (id, releasedBy) =>
    client
      .post(`/courses/${id}/release`, { released_by: releasedBy })
      .then((r) => r.data),
  updateCompany: (data) => client.put("/company", data).then((r) => r.data),
  createEmployee: (data) =>
    client.post("/employees", data).then((r) => r.data),
  updateEmployee: (id, data) =>
    client.patch(`/employees/${id}`, data).then((r) => r.data),
  archiveEmployee: (id, archived) =>
    client.post(`/employees/${id}/archive`, { archived }).then((r) => r.data),
  // WELT-ARCHIVIERUNG (Feb 2026, F1–F5): 1:1-Muster wie `archiveEmployee`.
  // Toggle-Endpoint für freigegebene Dokumentationswelten.
  // Response enthaelt `open_assignments`-Zaehler fuer F5-Confirm-Dialog.
  archiveCourse: (id, archived) =>
    client.post(`/courses/${id}/archive`, { archived }).then((r) => r.data),
  // CLARTEXT · Fuehrerscheinkontrolle · Block C — Read-Only Kategorienliste.
  // Quelle: services/fuehrerschein/vehicle_category_contract.py (byte-eingefroren).
  getVehicleCategories: () =>
    client.get("/fuehrerschein/vehicle-categories").then((r) => r.data),
  // CLARTEXT · Fuehrerscheinkontrolle · Block F (Feb 2026) — Mandanten-Freigabe.
  // Streng ADDITIV. Foto-Bytes werden ueber `axiosClient`-Level per fetch
  // gezogen, damit `Response` auf Blob-Ebene abgreifbar bleibt.
  listOpenFskRecords: () =>
    client.get("/fuehrerschein/records/open").then((r) => r.data),
  countOpenFskRecords: () =>
    client.get("/fuehrerschein/records/open/count").then((r) => r.data),
  // CLARTEXT · Block H (Feb 2026): Owner-lesbarer Modul-Add-on-Status
  // (Sidebar-Gate E9 + Zahlstatus-Banner). READ-ONLY.
  getModuleAddonStatus: (moduleId) =>
    client.get(`/module-addons/${moduleId}/status`).then((r) => r.data),
  // Handschutzplan-Modul · Baustein 5 (Aug 2026) — Owner-UI. Alle vier
  // Endpunkte sind server-seitig hazmat_addon-gegated (403 fail-closed).
  listHandschutzplaene: () =>
    client.get("/handschutzplaene").then((r) => r.data),
  // Baustein 7 — Read-Time-Fälligkeits-Summary für den Dashboard-Hinweis.
  getHandschutzplanFaelligkeit: () =>
    client.get("/handschutzplaene/faelligkeit-summary").then((r) => r.data),
  // Baustein 9-Erweiterung (Feb 2026): LUMPE-Datenqualitäts-Hinweis.
  // Zählt Registry-Einträge ohne Einsatzbereichs-Zuordnung.
  getHandschutzplanRegistryLuecken: () =>
    client.get("/handschutzplaene/registry-luecken").then((r) => r.data),
  // Gefahrstoffverzeichnis · Owner-Leseansicht (Aug 2026, § 6 GefStoffV).
  // Server-seitig hazmat_addon-gegated (403 fail-closed).
  listGefahrstoffverzeichnis: () =>
    client.get("/gefahrstoffverzeichnis").then((r) => r.data),
  // Register-PDF · Einzeldownload (30.08.2026) — dieselbe Live-Ableitung
  // wie 06_Gefahrstoffe/Register.pdf im BG-Prüfpaket-ZIP, kein WORM.
  fetchGefahrstoffRegisterPdfBlob: () =>
    client
      .get("/gefahrstoffverzeichnis/register.pdf", { responseType: "blob" })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // HSD-Substitutionserklärungen · BG-Prüfmappe Schritt 2 (28.08.2026).
  listSubstitutionZeugen: () =>
    client.get("/gefahrstoffverzeichnis/substitution-zeugen").then((r) => r.data),
  fetchSubstitutionWormBlob: (documentNumber) =>
    client
      .get(`/gefahrstoffverzeichnis/substitution-worm/${documentNumber}`, {
        responseType: "blob",
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // UX-Korrektur (29.08.2026): lesbarer PDF-Abzug des HSD-Zeugen.
  fetchSubstitutionBelegPdfBlob: (documentNumber) =>
    client
      .get(
        `/gefahrstoffverzeichnis/substitution-worm/${documentNumber}/pdf`,
        { responseType: "blob" }
      )
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // SDB-Original-Zeugen · BG-Prüfmappe Schritt 3 (29.08.2026).
  listSdbZeugen: () =>
    client.get("/gefahrstoffverzeichnis/sdb-zeugen").then((r) => r.data),
  fetchSdbOriginalPdfBlob: (documentNumber) =>
    client
      .get(`/gefahrstoffverzeichnis/sdb-original/${documentNumber}`, {
        responseType: "blob",
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // Baustein 8 — Owner-einstellbares Prüfintervall (3/6/9/12 Monate).
  getHandschutzplanEinstellungen: () =>
    client.get("/handschutzplaene/einstellungen").then((r) => r.data),
  updateHandschutzplanEinstellungen: (payload) =>
    client.put("/handschutzplaene/einstellungen", payload).then((r) => r.data),
  getHandschutzplan: (einsatzbereichId) =>
    client.get(`/handschutzplaene/${einsatzbereichId}`).then((r) => r.data),
  generateHandschutzplan: (einsatzbereichId) =>
    client
      .post(`/handschutzplaene/${einsatzbereichId}/generate`)
      .then((r) => r.data),
  fetchHandschutzplanWormBlob: (documentNumber) =>
    client
      .get(`/handschutzplaene/worm/${documentNumber}`, {
        responseType: "blob",
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // UX-Korrektur (29.08.2026): lesbarer PDF-Abzug des WORM-Zeugen —
  // primäre „Beleg öffnen"-Aktion. Das JSON bleibt der bindende Zeuge.
  fetchHandschutzplanBelegPdfBlob: (documentNumber) =>
    client
      .get(`/handschutzplaene/worm/${documentNumber}/pdf`, {
        responseType: "blob",
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // Baustein 6: Plakat-PDF (Format-Wahl 2×2) + interne Druckdaten (Token).
  downloadHandschutzplanPlakat: (einsatzbereichId, format, orientation) =>
    client
      .get(`/handschutzplaene/${einsatzbereichId}/plakat-pdf`, {
        responseType: "blob",
        params: { format, orientation },
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  getHandschutzplanDruckDaten: (renderToken) =>
    client
      .get(`/druck/handschutzplan-daten/${renderToken}`)
      .then((r) => r.data),
  getFskRecordDetail: (id) =>
    client.get(`/fuehrerschein/records/${id}`).then((r) => r.data),
  // BUGFIX 2026-08-09: <img src="URL"> umgeht den axios-Interceptor
  // (`useEmployeeSession.jsx:174–191`), der den Owner-Bearer anhaengt — der
  // native Browser-Fetch schickt keinen Authorization-Header und laeuft in
  // 401. Frage 7b bleibt (Backend-Proxy-Stream), aber das UI holt die
  // Bytes explizit ueber `client` (Auth-Interceptor greift) und bindet sie
  // via `URL.createObjectURL` an das <img>-Element.
  fetchFskPhotoBlob: (id, side) =>
    client
      .get(`/fuehrerschein/records/${id}/photo/${side}`, {
        responseType: "blob",
      })
      .then((r) => r.data),
  // CLARTEXT · Fuehrerscheinkontrolle · Block F Nachtrag (Feb 2026) —
  // Historie entschiedener Records + Beweis-Download. Alle drei Endpunkte
  // laufen ueber `client` (Owner-Bearer greift ueber den Auth-Interceptor).
  listDecidedFskRecords: (params = {}) =>
    client
      .get("/fuehrerschein/records/decided", { params })
      .then((r) => r.data),
  countDecidedFskRecords: (params = {}) =>
    client
      .get("/fuehrerschein/records/decided/count", { params })
      .then((r) => r.data),
  fetchFskWormJsonBlob: (id) =>
    client
      .get(`/fuehrerschein/records/${id}/worm.json`, {
        responseType: "blob",
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  // Block F Nachtrag II (Feb 2026) — menschenlesbarer PDF-Nachweis je Record.
  // Nutzt dasselbe WeasyPrint-Muster wie ``services/pdf_generator``.
  fetchFskNachweisPdfBlob: (id) =>
    client
      .get(`/fuehrerschein/records/${id}/nachweis.pdf`, {
        responseType: "blob",
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  fetchFskDecidedWormZipBlob: (params = {}) =>
    client
      .get("/fuehrerschein/records/decided/worm.zip", {
        params,
        responseType: "blob",
      })
      .then((r) => ({ blob: r.data, headers: r.headers })),
  confirmFskRecord: (id, decision, note = "") =>
    client
      .post(`/fuehrerschein/records/${id}/confirm`, { decision, note })
      .then((r) => r.data),
  getFskPhotoUrl: (id, side) =>
    `${client.defaults.baseURL}/fuehrerschein/records/${id}/photo/${side}`,
  listEmployeesAll: () =>
    client.get("/employees", { params: { include_archived: true } }).then((r) => r.data),
  // UI-REDESIGN Master-Detail (Aug 2026): Einzel-Mitarbeiter für die
  // Detailseite — bestehender Backend-Endpunkt GET /api/employees/{id}.
  getEmployee: (id) => client.get(`/employees/${id}`).then((r) => r.data),
  getDemoStatus: () => client.get("/demo/status").then((r) => r.data),
  toggleDemo: (visible) =>
    client.post("/demo/toggle", { visible }).then((r) => r.data),
  // Sprint M.0 (Juni 2026) – Demo-Werkszustand-Reset (nur Demo-Mandanten).
  demoFactoryReset: () =>
    client.post("/demo/factory-reset").then((r) => r.data),
  // Phase 3 — Einsatzbereiche (Stammdaten + Welt-Verteilung)
  listEinsatzbereiche: (includeArchived = true) =>
    client
      .get("/einsatzbereiche", { params: { include_archived: includeArchived } })
      .then((r) => r.data),
  createEinsatzbereich: (data) =>
    client.post("/einsatzbereiche", data).then((r) => r.data),
  updateEinsatzbereich: (id, data) =>
    client.patch(`/einsatzbereiche/${id}`, data).then((r) => r.data),
  archiveEinsatzbereich: (id, archived) =>
    client
      .post(`/einsatzbereiche/${id}/archive`, { archived })
      .then((r) => r.data),
  // SP-1.2 (Juni 2026) – kontext-angereicherte KI-Weltvorschläge für ein
  // Arbeitsfeld. REIN LESEND: erzeugt/ändert nichts, löst keine
  // Generierung/Release/Zuweisung aus (das passiert erst bei „Übernehmen").
  listWeltVorschlaege: (einsatzbereichId) =>
    client
      .get(`/courses/recommendations`, {
        params: { einsatzbereich_id: einsatzbereichId },
      })
      .then((r) => r.data),
  updateCourseEinsatzbereiche: (id, einsatzbereichIds) =>
    client
      .patch(`/courses/${id}/einsatzbereiche`, {
        einsatzbereich_ids: einsatzbereichIds,
      })
      .then((r) => r.data),
  // Phase E4 (Juni 2026) – Bestands-Assistent: Dokumentationen ohne
  // Einsatzbereich-Zuordnung + Hybrid-Vorschlag (KI/Katalog). REIN LESEND.
  listEinsatzbereichLuecken: () =>
    client.get(`/courses/einsatzbereich-luecken`).then((r) => r.data),
  getEinsatzbereichVorschlag: (id) =>
    client.get(`/courses/${id}/einsatzbereich-vorschlag`).then((r) => r.data),
  updateCourse: (id, patch) =>
    client.patch(`/courses/${id}`, patch).then((r) => r.data),
  // P0-D3 (2026-07-16) — Read-only Freigabe-Readiness (Ampelvertrag V1.0).
  // Liefert {status, ready, violations[], contract_version, ampel_version, checked_at}.
  // Keine Businesslogik im Frontend: Farben ausschließlich aus `status`.
  getReleaseReadiness: (id) =>
    client.get(`/courses/${id}/release-readiness`).then((r) => r.data),
  releaseNewVersion: (id, patch) =>
    client
      .post(`/courses/${id}/release-new-version`, patch || {})
      .then((r) => r.data),
  deleteCourse: (id) => client.delete(`/courses/${id}`).then((r) => r.data),
  retryCourse: (id) =>
    client.post(`/courses/${id}/retry`).then((r) => r.data),
  // Phase 5 — Setup-Wizard (Onboarding)
  getSetupStatus: () => client.get("/setup/status").then((r) => r.data),
  // WELT-01b (Okt 2026): eigenes Welten-Kontingent (read-only) für die
  // Bibliotheks-Anzeige „X von Y eigenen Welten genutzt".
  getWorldQuota: () => client.get("/courses/world-quota").then((r) => r.data),
  // WELT-02b (Betreiberfreigabe 12.10.): Welten-Paket-Checkout (10 Welten, Einmalkauf).
  // Body NUR `origin_url`; Preis/Produkt/Steuersatz/Stripe-Parameter kommen serverseitig
  // aus dem veröffentlichten Katalog. Owner-Route (axios-Interceptor hängt den Token an).
  createWeltenPaketCheckout: (origin_url) =>
    client.post("/checkout/welten-paket", { origin_url }).then((r) => r.data),
  listBranchen: () => client.get("/setup/branchen").then((r) => r.data),
  // Sprint L (Juni 2026) – KI-Vorschlagsliste aus freier Betriebsbeschreibung.
  suggestWorlds: (business_description) =>
    client
      .post("/setup/suggest-worlds", { business_description })
      .then((r) => r.data),
  completeSetup: (payload) =>
    client.post("/setup/complete", payload).then((r) => r.data),
  // Universal-Welten (Juni 2026) – ZUSÄTZLICHER Zweig zum Setup-Wizard:
  // freigegebene Welten des Master-Mandanten lesen / je Welt übernehmen.
  listUniversalWorlds: () =>
    client.get("/setup/universal-worlds").then((r) => r.data),
  adoptUniversalWorlds: (world_ids) =>
    client
      .post("/setup/adopt-universal-worlds", { world_ids })
      .then((r) => r.data),
  // Phase D2 (Juni 2026) – Demo → eigener Betrieb → Demo automatisch entfernen.
  demoUpgrade: () => client.post("/demo/upgrade").then((r) => r.data),
  discardDemoAfterUpgrade: (demoTenantId) =>
    client
      .post("/demo/discard-after-upgrade", { demo_tenant_id: demoTenantId })
      .then((r) => r.data),
  // Phase 6 — PIN-Login + Mitarbeiter-Self-Service
  // Sprint G.2 (Juni 2026): Login = Firmen-Code + PIN (Mandanten-Kontext).
  employeeLogin: (companyCode, pin) =>
    client
      .post("/auth/employee-login", { company_code: companyCode, pin })
      .then((r) => r.data),
  employeeLogout: () =>
    client.post("/auth/employee-logout").then((r) => r.data),
  getMe: () => client.get("/auth/me").then((r) => r.data),
  listMyAssignments: () => client.get("/me/assignments").then((r) => r.data),
  // Neu-seit-letztem-Besuch (Juni 2026): Liste als gesehen markieren (idempotent).
  markTasksSeen: () => client.post("/me/assignments/seen").then((r) => r.data),
  // Mitarbeiter-Einstellungen Teil 2 (Juni 2026): nur eigener Datensatz.
  updateMySettings: (payload) => client.patch("/me/settings", payload).then((r) => r.data),
  // CLARTEXT · Fuehrerscheinkontrolle · Block D — Mitarbeiter-Endpoints
  getMyDriverTask: () => client.get("/me/driver-task").then((r) => r.data),
  getMyVehicleCategories: () =>
    client.get("/me/vehicle-categories").then((r) => r.data),
  submitMyDriverTask: (formData) =>
    client
      .post("/me/driver-task/submit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  // CLARTEXT · Fuehrerscheinkontrolle · Block E — OCR-Lesehilfe (Read/Trigger).
  // Endpoint startet lazy einen Background-Task, wenn noch keine Reading da ist.
  getMyDriverOcr: () => client.get("/me/driver-task/ocr").then((r) => r.data),
  getMyAssignment: (id) =>
    client.get(`/me/assignments/${id}`).then((r) => r.data),
  completeMyAssignment: (id, answers) =>
    client
      .post(`/me/assignments/${id}/complete`, { answers })
      .then((r) => r.data),
  // Sprint F (Feb 2026) – Mitarbeiter-Nachschlagewerk
  searchMyWorlds: (q) =>
    client.get(`/me/worlds`, { params: { q } }).then((r) => r.data),
  getMyWorld: (id) => client.get(`/me/worlds/${id}`).then((r) => r.data),
  // PIN-Verwaltung (Unternehmer-Sicht)
  setEmployeePin: (id, pin) =>
    client.post(`/employees/${id}/pin`, { pin }).then((r) => r.data),
  regenerateEmployeePin: (id) =>
    client.post(`/employees/${id}/pin/regenerate`).then((r) => r.data),
  // Sprint A.5 — Unternehmer-Master-PIN-Login
  // Sprint G.2 (Juni 2026): Login = Firmen-Code + PIN.
  ownerLogin: (companyCode, pin) =>
    client
      .post("/auth/owner-login", { company_code: companyCode, pin })
      .then((r) => r.data),
  ownerLogout: () =>
    client.post("/auth/owner-logout").then((r) => r.data),
  getOwnerMe: () => client.get("/auth/owner/me").then((r) => r.data),
  hasDefaultOwnerPin: (companyCode) =>
    client
      .get("/auth/owner/has-default-pin", {
        params: companyCode ? { company_code: companyCode } : {},
      })
      .then((r) => r.data),
  ownerChangePin: (currentPin, newPin) =>
    client
      .post("/auth/owner/change-pin", {
        current_pin: currentPin,
        new_pin: newPin,
      })
      .then((r) => r.data),
  // IMPLEMENT-11 (Juni 2026) – isolierter KI-Lehrbild-Prototyp (Owner-gated).
  startPreviewImages: () =>
    client.post("/preview-images/generate").then((r) => r.data),
  getPreviewImagesStatus: (jobId) =>
    client.get(`/preview-images/status/${jobId}`).then((r) => r.data),
  getLatestPreviewImages: () =>
    client.get("/preview-images/latest").then((r) => r.data),
  // IMPLEMENT-12 (Juni 2026) – Stil-Härtung (CLARTEXT-Bildsprache, Alt/Neu-Vergleich).
  startStilImages: () =>
    client.post("/preview-images/stil/generate").then((r) => r.data),
  getLatestStilImages: () =>
    client.get("/preview-images/stil/latest").then((r) => r.data),
  // IMPLEMENT-13 (Juni 2026) – Stil-Anker-Test (Referenzbild aus IMPLEMENT-12).
  startAnchorImages: () =>
    client.post("/preview-images/anker/generate").then((r) => r.data),
  getLatestAnchorImages: () =>
    client.get("/preview-images/anker/latest").then((r) => r.data),
  // IMPLEMENT-14 (Juni 2026) – KI-Bilder in bestehende Lernkacheln einer Welt.
  startWeltImages: (worldId) =>
    client
      .post("/preview-images/welt/generate", null, worldId ? { params: { world_id: worldId } } : undefined)
      .then((r) => r.data),
  getLatestWeltImages: () =>
    client.get("/preview-images/welt/latest").then((r) => r.data),
  getLehrbildManifest: (worldId) =>
    client.get(`/preview-images/manifest/${worldId}`).then((r) => r.data),

  // CLARTEXT · A1 (Feb 2026) — Aushangpflicht-Anzeige.
  // Mitarbeiter-Endpoint (Employee-JWT wird vom Interceptor in
  // useEmployeeSession über die routeKind("/me/*")-Regel automatisch angehängt).
  getMyAushang: () => client.get("/me/aushang").then((r) => r.data),
  // Handschutzplan-Modul · Baustein 9 (Feb 2026): Mitarbeiter-Leseansicht.
  getMyHandschutzplaene: () =>
    client.get("/me/handschutzplaene").then((r) => r.data),
  // Gefahrstoffverzeichnis · Mitarbeiter-Pflichtangaben je Einsatzbereich
  // (Aug 2026, § 6 Abs. 12 Satz 4 GefStoffV). Server-seitig gegated +
  // whitelist-gefiltert.
  getMyGefahrstoffe: (einsatzbereichId) =>
    client
      .get(`/me/gefahrstoffverzeichnis/${einsatzbereichId}`)
      .then((r) => r.data),
  // Plakat-First · Async-Umstellung (28.02.2026, 2. Kurskorrektur):
  // Zwei-Schritt-Flow — prepare → Poll auf plakat-status bis
  // ready/error → dann Render-Endpunkt. Der Render-Endpunkt liefert
  // 409 ``prepare_required``, wenn der Client den Prepare-Schritt
  // übersprungen hat.
  //
  // Owner-Seite (HandschutzplanDetail.jsx):
  preparePlakat: (einsatzbereichId) =>
    client
      .post(`/handschutzplaene/${einsatzbereichId}/plakat/prepare`)
      .then((r) => r.data),
  getPlakatStatus: (einsatzbereichId) =>
    client
      .get(`/handschutzplaene/${einsatzbereichId}/plakat-status`)
      .then((r) => r.data),
  // (Owner-Download-Route unverändert im Namen — nur der Server-Vertrag
  // hat sich geändert: 200 bei Reuse, 409 bei fehlenden Bildern.)
  //
  // Mitarbeiter-Seite (MonteurHandschutzplan.jsx):
  prepareMyPlakat: (einsatzbereichId) =>
    client
      .post(`/me/handschutzplaene/${einsatzbereichId}/plakat/prepare`)
      .then((r) => r.data),
  getMyPlakatStatus: (einsatzbereichId) =>
    client
      .get(`/me/handschutzplaene/${einsatzbereichId}/plakat-status`)
      .then((r) => r.data),
  // Render-Fetch für die Mitarbeiter-JPEG. Nach Async-Umstellung liefert
  // der Endpunkt nur noch entweder JPEG (Reuse) oder 409 ``prepare_required``
  // (nach prepare-Aufruf sollte 200 folgen, sobald Poll ``ready`` meldet).
  fetchMyHandschutzplanPlakatJpeg: (einsatzbereichId) =>
    client
      .get(`/me/handschutzplaene/${einsatzbereichId}/plakat.jpg`, {
        responseType: "blob",
        // Render-Phase selbst ist kurz (Chromium 2-5 s). Konservativer
        // Timeout für den warmen Reuse-Pfad.
        timeout: 30000,
        validateStatus: (status) =>
          status === 200 || status === 403 || status === 409,
      })
      .then((r) => ({ status: r.status, blob: r.data })),
  // Owner-Endpoint (Owner-Bearer via geteiltem `client`).
  getOwnerAushang: () => client.get("/owner/aushang").then((r) => r.data),
};
